import { Controller } from "@hotwired/stimulus"

// Admin › Subscriptions › Owner plans (Phelipe 2026-10-31): the Add plan
// pop-up (company search, name, amount, currency, monthly / yearly) and the
// bin on each row (after a confirm). Rows go in and out without reloading;
// the # column is renumbered from the server's answer (n/a for ConsLog's
// test company).
export default class extends Controller {
  static targets = [
    "rows", "empty", "modal", "companySearch", "companyId", "results", "name", "amount", "currency",
    "interval", "error", "saveButton", "removeModal", "removeLabel", "removeError", "removeButton"
  ]
  static values = { createUrl: String, companiesUrl: String }

  open() {
    this.companySearchTarget.value = ""
    this.companyIdTarget.value = ""
    this.nameTarget.value = ""
    this.nameTarget.placeholder = "Plan for the company"
    this.amountTarget.value = ""
    this.currencyTarget.value = "USD"
    this.intervalTarget.value = "month"
    this.errorTarget.classList.add("hidden")
    this._hideResults()
    this.modalTarget.classList.remove("hidden")
    this.companySearchTarget.focus()
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Companies whose name has what's typed (2+ letters).
  searchCompanies() {
    this.companyIdTarget.value = ""
    clearTimeout(this._searchTimer)
    const query = this.companySearchTarget.value.trim()
    if (query.length < 2) return this._hideResults()

    this._searchTimer = setTimeout(async () => {
      const response = await fetch(`${this.companiesUrlValue}?q=${encodeURIComponent(query)}`, { headers: { Accept: "application/json" } })
      const companies = response.ok ? await response.json() : []
      if (query === this.companySearchTarget.value.trim()) this._showResults(companies)
    }, 200)
  }

  async save() {
    if (!this.companyIdTarget.value) {
      this.errorTarget.textContent = "Pick a company from the list."
      this.errorTarget.classList.remove("hidden")
      return
    }
    const body = {
      company_id: this.companyIdTarget.value, name: this.nameTarget.value, amount: this.amountTarget.value,
      currency: this.currencyTarget.value, interval: this.intervalTarget.value
    }
    await this._send(this.createUrlValue, "POST", body, this.saveButtonTarget, this.errorTarget, (data) => {
      this.rowsTarget.insertAdjacentHTML("afterbegin", data.row_html)
      this.emptyTarget.classList.add("hidden")
      this._renumber(data.numbers)
      this.close()
      this._showToast("Owner plan added.")
    })
  }

  askRemove(event) {
    const button = event.currentTarget
    this.removing = { url: button.dataset.url, row: button.closest("tr") }
    this.removeLabelTarget.textContent = button.dataset.label || ""
    this.removeErrorTarget.classList.add("hidden")
    this.removeModalTarget.classList.remove("hidden")
  }

  closeRemove() {
    this.removeModalTarget.classList.add("hidden")
  }

  async confirmRemove() {
    const removing = this.removing
    if (!removing) return
    await this._send(removing.url, "DELETE", {}, this.removeButtonTarget, this.removeErrorTarget, (data) => {
      removing.row.remove()
      this._renumber(data.numbers)
      if (!this.rowsTarget.querySelector("tr[data-plan-id]")) this.emptyTarget.classList.remove("hidden")
      this.closeRemove()
      this._showToast("Owner plan removed.")
    })
  }

  // ── helpers ──

  _showResults(companies) {
    this.resultsTarget.replaceChildren()
    if (companies.length === 0) {
      const none = document.createElement("p")
      none.className = "px-3 py-1.5 text-[13px] text-text-muted"
      none.textContent = "No company with that name."
      this.resultsTarget.appendChild(none)
    }
    companies.forEach((company) => {
      const option = document.createElement("button")
      option.type = "button"
      option.className = "block w-full px-3 py-1.5 text-left text-[13px] text-gray-700 hover:bg-gray-50"
      option.textContent = company.name
      option.addEventListener("click", () => this._pick(company))
      this.resultsTarget.appendChild(option)
    })
    this.resultsTarget.classList.remove("hidden")
  }

  _hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.replaceChildren()
  }

  _pick(company) {
    this.companyIdTarget.value = company.id
    this.companySearchTarget.value = company.name
    this.currencyTarget.value = company.currency
    this.nameTarget.placeholder = `Plan for ${company.name}`
    this.errorTarget.classList.add("hidden")
    this._hideResults()
    this.amountTarget.focus()
  }

  _renumber(numbers) {
    this.rowsTarget.querySelectorAll("tr[data-plan-id]").forEach((row) => {
      const cell = row.querySelector("[data-number]")
      if (cell) cell.textContent = (numbers || {})[row.dataset.planId] || "n/a"
    })
  }

  async _send(url, method, body, button, errorEl, onSuccess) {
    button.disabled = true
    errorEl.classList.add("hidden")
    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "Something went wrong. Try again.")
      onSuccess(data)
    } catch (error) {
      errorEl.textContent = error.message
      errorEl.classList.remove("hidden")
    } finally {
      button.disabled = false
    }
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
